import formatCurrency from 'format-currency';
import addFundingPairModal from '../../../../../common/modals/addFundingPairModal/addFundingPairModal.component';
import addObligationBasisLineModal from '../../../../../common/modals/addObligationBasisLineModal/addObligationBasisLineModal.component';
import manageObligationBasisLinesModal from '../../../../../common/modals/manageObligationBasisLinesModal/manageObligationBasisLinesModal.component';
import reassignFundingLineModal from '../../../../../common/modals/reassignFundingLineModal/reassignFundingLineModal.component';
import unassociatedFundingLinesModal from '../../../../../common/modals/unassociatedFundingLinesModal/unassociatedFundingLinesModal.component';
import viewObligationAttributionModal from '../../../../../common/modals/viewObligationAttributionModal/viewObligationAttributionModal.component';

class ObligationFundingPanelController {
  constructor($http, $rootScope, $uibModal, $q, toastr, AuthenticationService) {
    'ngInject';
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$uibModal = $uibModal;
    this.$q = $q;
    this.toastr = toastr;
    this.auth = AuthenticationService;
    this.loading = false;
    this.migrating = false;
    this.obligations = [];
    this.expandedObligations = {};
    this.basisLinesByObligation = {};
    this.basisLinesLoading = {};
    this.basisLineSaving = {};
    this.basisLineBalancing = {};
    this.basisLineSortByObligation = {};
    this.obligationSort = {
      field: 'clin',
      reverse: false
    };
    this.sortedObligations = [];
    this.fundingLineSort = {
      field: 'lineNumber',
      reverse: false
    };
    // Stable predicate reference so Angular orderBy does not infinite-digest.
    this.fundingLineOrderBy = (line) => {
      return this.fundingLineSortValue(line, this.fundingLineSort.field);
    };
    this.fundingLineGroups = [];
    this.selectedFundingLineGroupKey = null;
    this.selectedFundingLineGroup = null;
    this.fundingLineGroupBalancing = false;
    this.selectedFundingComponentTab = 'LABOR';
  }

  $onChanges(changes) {
    if (changes.project && changes.project.currentValue && changes.project.currentValue.id) {
      this.loadObligations();
      this.rebuildFundingLineGroups();
    }
  }

  canManage() {
    return this.auth.hasPermission('PERM_MANAGE_PROJECT_FUNDING_DETAILS');
  }

  /**
   * Show at least 2 decimals; include up to maxDigits only while trailing digits are non-zero.
   * e.g. 10 -> 2, 10.5 -> 2, 10.123 -> 3, 10.12340 -> 4, 10.12345 -> 5
   */
  getDisplayFractionDigits(value, maxDigits = 5, minDigits = 2) {
    const num = Number(value);
    if (!isFinite(num)) {
      return minDigits;
    }
    const decimalPart = Math.abs(num).toFixed(maxDigits).split('.')[1] || '';
    let digits = maxDigits;
    while (digits > minDigits && decimalPart.charAt(digits - 1) === '0') {
      digits--;
    }
    return digits;
  }

  formatMoney(value) {
    const num = Number(value) || 0;
    const digits = this.getDisplayFractionDigits(num);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(num);
  }

  currency(value) {
    return formatCurrency(value);
  }

  formatHours(value) {
    const num = Number(value);
    if (!isFinite(num)) {
      return '0.00';
    }
    const digits = this.getDisplayFractionDigits(num);
    return num.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  getFullyBurdenedRate(line) {
    return Number(line && line.chargeCode && line.chargeCode.popRate
      && line.chargeCode.popRate.fullyBurdenedRate) || 0;
  }

  getFullyBurdenedRateInclusiveOfFee(line) {
    return Number(line && line.chargeCode && line.chargeCode.popRate
      && line.chargeCode.popRate.fullyBurdenedRateInclusiveOfFee) || 0;
  }

  getFeeRate(line) {
    return Number(line && line.chargeCode && line.chargeCode.popRate
      && line.chargeCode.popRate.feeRate) || 0;
  }

  getActualHours(line) {
    return Number(line && line.cumulativeHours) || 0;
  }

  getFundedLaborAmount(line) {
    return this.getFullyBurdenedRate(line) * (Number(line && line.fundedHours) || 0);
  }

  getFundedFeeAmount(line) {
    return this.getFeeRate(line) * (Number(line && line.fundedHours) || 0);
  }

  getLineRate(line) {
    if (line && line.fundingComponent === 'FEE') {
      return this.getFeeRate(line);
    }
    if (line && line.fundingComponent === 'LABOR') {
      return this.getFullyBurdenedRate(line);
    }
    // Legacy combined lines: show fully burdened rate inclusive of fee.
    return this.getFullyBurdenedRateInclusiveOfFee(line);
  }

  getLineRateLabel(line) {
    if (line && line.fundingComponent === 'FEE') {
      return 'Fixed Fee / Hour';
    }
    if (line && line.fundingComponent === 'LABOR') {
      return 'Fully Burdened';
    }
    return 'Fully Burdened w/ Fee';
  }

  getTotalFundedAmount(line) {
    return this.getLineRate(line) * (Number(line && line.fundedHours) || 0);
  }

  getActualSpentAmount(line) {
    return this.getLineRate(line) * this.getActualHours(line);
  }

  getRemainingHours(line) {
    return (Number(line && line.fundedHours) || 0) - this.getActualHours(line);
  }

  getRemainingFunding(line) {
    return this.getTotalFundedAmount(line) - this.getActualSpentAmount(line);
  }

  isRemainingHoursNegative(line) {
    return this.getRemainingHours(line) < -0.009;
  }

  isRemainingFundingNegative(line) {
    return this.getRemainingFunding(line) < -0.009;
  }

  getFundingLineFixedFeeBasisId(line) {
    return line && line.chargeCode && line.chargeCode.popRate
      && line.chargeCode.popRate.fixedFeeBasisId;
  }

  getObligationAllocatedAmountForLine(line, obligation) {
    if (!obligation) {
      return 0;
    }
    const basisId = this.getFundingLineFixedFeeBasisId(line);
    const basisLines = this.getBasisLines(obligation.id);
    return basisLines
      .filter((basisLine) => !basisId || String(basisLine.fixedFeeBasisId) === String(basisId))
      .reduce((sum, basisLine) => sum + (Number(basisLine.fundedAmount) || 0), 0);
  }

  getObligationAttributedAmountUsedByOthers(line, obligation) {
    if (!line || !obligation) {
      return 0;
    }
    const basisId = this.getFundingLineFixedFeeBasisId(line);
    return this.getFundingLines().reduce((sum, otherLine) => {
      if (!otherLine || String(otherLine.id) === String(line.id)) {
        return sum;
      }
      if (basisId && String(this.getFundingLineFixedFeeBasisId(otherLine)) !== String(basisId)) {
        return sum;
      }
      const amount = (otherLine.obligationAllocations || [])
        .filter((allocation) => String(allocation.contractFundingObligationId) === String(obligation.id))
        .reduce((inner, allocation) => {
          if (allocation.amount != null && allocation.amount !== '') {
            return inner + (Number(allocation.amount) || 0);
          }
          return inner + ((Number(allocation.hours) || 0) * this.getLineRate(otherLine));
        }, 0);
      return sum + amount;
    }, 0);
  }

  getAttributionCapacity(line, obligation) {
    const allocatedAmount = this.getObligationAllocatedAmountForLine(line, obligation);
    const usedByOthers = this.getObligationAttributedAmountUsedByOthers(line, obligation);
    return {
      allocatedAmount,
      usedByOthers,
      availableAmount: allocatedAmount - usedByOthers
    };
  }

  getActualLaborCost(line) {
    return this.getFullyBurdenedRate(line) * this.getActualHours(line);
  }

  getActualFeeCost(line) {
    return this.getFeeRate(line) * this.getActualHours(line);
  }

  workTypeLabel(workType) {
    if (workType === 'COMPLETION') {
      return 'Completion';
    }
    if (workType === 'LEVEL_OF_EFFORT') {
      return 'Level of Effort';
    }
    return workType || '—';
  }

  costElementLabel(obligation) {
    return obligation.costElementDisplayName || obligation.costElement || '—';
  }

  sortObligations(field, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.obligationSort) {
      this.obligationSort = { field: 'clin', reverse: false };
    }
    if (this.obligationSort.field === field) {
      this.obligationSort.reverse = !this.obligationSort.reverse;
    } else {
      this.obligationSort.field = field;
      this.obligationSort.reverse = false;
    }
    this.applyObligationSort();
  }

  obligationSortIcon(field) {
    if (!this.obligationSort || this.obligationSort.field !== field) {
      return 'fa-sort';
    }
    return this.obligationSort.reverse ? 'fa-sort-desc' : 'fa-sort-asc';
  }

  obligationSortValue(obligation, field) {
    if (!obligation) {
      return null;
    }
    switch (field) {
      case 'clin':
      case 'slin':
      case 'acrn':
        return obligation[field] == null || obligation[field] === ''
          ? ''
          : String(obligation[field]);
      case 'costElement':
        return this.costElementLabel(obligation);
      case 'breakout':
        return this.getObligationLineCount(obligation) === '…'
          ? -1
          : Number(this.getObligationLineCount(obligation)) || 0;
      case 'lineCount':
        return this.getObligationLineCount(obligation);
      case 'laborHours':
        return this.getObligationLaborHours(obligation);
      case 'feeHours':
        return this.getObligationFeeHours(obligation);
      case 'obligated':
        return Number(obligation.obligatedAmount) || 0;
      case 'allocated':
        return this.getObligationAttributedAmount(obligation);
      case 'available':
        return this.getObligationAvailableAmount(obligation);
      default:
        return '';
    }
  }

  compareObligationSortValues(a, b) {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    const sa = a == null ? '' : String(a);
    const sb = b == null ? '' : String(b);
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
  }

  applyObligationSort() {
    const field = (this.obligationSort && this.obligationSort.field) || 'clin';
    const reverse = !!(this.obligationSort && this.obligationSort.reverse);
    const dir = reverse ? -1 : 1;

    this.sortedObligations = (this.obligations || []).slice().sort((left, right) => {
      let cmp = this.compareObligationSortValues(
        this.obligationSortValue(left, field),
        this.obligationSortValue(right, field)
      );
      if (cmp !== 0) {
        return cmp * dir;
      }

      // Default / CLIN sort: keep SLIN ascending as the tiebreaker.
      if (field === 'clin') {
        cmp = this.compareObligationSortValues(
          this.obligationSortValue(left, 'slin'),
          this.obligationSortValue(right, 'slin')
        );
        if (cmp !== 0) {
          return cmp;
        }
      }

      return (Number(left.id) || 0) - (Number(right.id) || 0);
    });
  }

  fundingBasisLabel(obligation) {
    if (!obligation || !obligation.fundingBasis) {
      return '—';
    }
    if (obligation.fundingBasis === 'ACTUALS') {
      return 'Actuals';
    }
    if (obligation.fundingBasis === 'CONTRACT_RATES') {
      return 'Contract Rates';
    }
    return obligation.fundingBasis;
  }

  componentLabel(line) {
    if (!line || !line.fundingComponent) {
      return 'Combined';
    }
    if (line.fundingComponent === 'LABOR') {
      return 'Labor';
    }
    if (line.fundingComponent === 'FEE') {
      return 'Fee';
    }
    return line.fundingComponent;
  }

  isLaborObligation(obligation) {
    return !!(obligation && obligation.costElement === 'LABOR');
  }

  isFeeObligation(obligation) {
    return !!(obligation && obligation.costElement === 'FIXED_FEE');
  }

  expectedComponentForObligation(obligation) {
    if (this.isLaborObligation(obligation)) {
      return 'LABOR';
    }
    if (this.isFeeObligation(obligation)) {
      return 'FEE';
    }
    return null;
  }

  isLineOutOfPlace(line, obligation) {
    if (!line || !obligation) {
      return false;
    }
    const expected = this.expectedComponentForObligation(obligation);
    if (!expected) {
      return false;
    }
    if (!line.fundingComponent) {
      return true;
    }
    return line.fundingComponent !== expected;
  }

  outOfPlaceWarning(line, obligation) {
    if (!this.isLineOutOfPlace(line, obligation)) {
      return '';
    }
    const obligationType = this.costElementLabel(obligation);
    if (!line.fundingComponent) {
      return `Combined line on a ${obligationType} obligation — split or reassign.`;
    }
    return `${this.componentLabel(line)} line on a ${obligationType} obligation.`;
  }

  supportsBasisLines(obligation) {
    return !!(obligation
      && obligation.fundingBasis === 'CONTRACT_RATES'
      && (obligation.costElement === 'LABOR' || obligation.costElement === 'FIXED_FEE'));
  }

  isObligationExpanded(obligationId) {
    return !!this.expandedObligations[obligationId];
  }

  toggleObligation(obligationId, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    const expanding = !this.expandedObligations[obligationId];
    this.expandedObligations[obligationId] = expanding;
    if (expanding) {
      this.loadBasisLines(obligationId);
    }
  }

  expandAllObligations() {
    (this.obligations || []).forEach((obligation) => {
      if (!this.supportsBasisLines(obligation)) {
        return;
      }
      this.expandedObligations[obligation.id] = true;
      this.loadBasisLines(obligation.id);
    });
  }

  collapseAllObligations() {
    this.expandedObligations = {};
  }

  getBasisLines(obligationId) {
    if (!Object.prototype.hasOwnProperty.call(this.basisLinesByObligation, obligationId)) {
      return [];
    }
    return this.basisLinesByObligation[obligationId];
  }

  getBasisLineSort(obligationId) {
    if (!this.basisLineSortByObligation[obligationId]) {
      this.basisLineSortByObligation[obligationId] = {
        field: 'laborCategoryName',
        reverse: false
      };
    }
    return this.basisLineSortByObligation[obligationId];
  }

  sortBasisLines(obligationId, field, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    const sort = this.getBasisLineSort(obligationId);
    if (sort.field === field) {
      sort.reverse = !sort.reverse;
    } else {
      sort.field = field;
      sort.reverse = false;
    }
  }

  basisLineSortIcon(obligationId, field) {
    const sort = this.getBasisLineSort(obligationId);
    if (sort.field !== field) {
      return 'fa-sort';
    }
    return sort.reverse ? 'fa-sort-desc' : 'fa-sort-asc';
  }

  basisLineSortValue(line, field) {
    if (!line) {
      return null;
    }
    if (field === 'laborCategoryName') {
      return (line.laborCategoryName == null || line.laborCategoryName === ''
        ? ''
        : String(line.laborCategoryName)).toLowerCase();
    }
    const num = Number(line[field]);
    return isFinite(num) ? num : 0;
  }

  getBasisLineOrderBy(obligationId) {
    const sort = this.getBasisLineSort(obligationId);
    if (!sort.orderBy) {
      sort.orderBy = (line) => {
        const current = this.getBasisLineSort(obligationId);
        return this.basisLineSortValue(line, current.field);
      };
    }
    return sort.orderBy;
  }

  isBasisLineSortReversed(obligationId) {
    return !!this.getBasisLineSort(obligationId).reverse;
  }

  isBasisLinesLoading(obligationId) {
    return !!this.basisLinesLoading[obligationId];
  }

  isBasisLineSaving(lineId) {
    return !!this.basisLineSaving[lineId];
  }

  isBasisLineBalancing(obligationId) {
    return !!this.basisLineBalancing[obligationId];
  }

  canBalanceBasisLines(obligation) {
    return this.canManage()
      && this.supportsBasisLines(obligation)
      && this.getBasisLines(obligation && obligation.id).length > 0
      && !this.isBasisLinesLoading(obligation && obligation.id)
      && !this.isBasisLineBalancing(obligation && obligation.id);
  }

  balanceBasisLineHours(obligation, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canBalanceBasisLines(obligation)) {
      return;
    }

    const obligated = this.formatMoney(obligation.obligatedAmount);
    const confirmed = window.confirm(
      'Balance funded hours across basis lines so total funded amount equals '
      + obligated + '?\n\n'
      + 'Existing hours will be scaled proportionally. If all hours are zero, '
      + 'the obligated amount is split evenly.'
    );
    if (!confirmed) {
      return;
    }

    this.basisLineBalancing[obligation.id] = true;
    this.$http.post(
      `${this.basisLinesUrl(obligation.id)}/balance`,
      {},
      { withCredentials: true }
    ).then((success) => {
      this.basisLinesByObligation[obligation.id] = success.data || [];
      this.rebuildFundingLineGroups();
      this.toastr.success('Funded hours balanced to obligated amount');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Failed to balance funded hours';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.basisLineBalancing[obligation.id] = false;
    });
  }

  basisRateTypeLabel(line) {
    if (!line) {
      return '';
    }
    if (line.rateType === 'FIXED_FEE') {
      return 'Fixed Fee / Hour';
    }
    if (line.rateType === 'FULLY_LOADED_LABOR') {
      return 'Fully Loaded Labor';
    }
    return line.rateType || '';
  }

  getBasisLinesHoursTotal(obligationId) {
    const total = this.getBasisLines(obligationId)
      .reduce((sum, line) => sum + (Number(line.fundedHours) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  getBasisLinesAmountTotal(obligationId) {
    const total = this.getBasisLines(obligationId)
      .reduce((sum, line) => sum + (Number(line.fundedAmount) || 0), 0);
    return Math.round(total * 100) / 100;
  }

  basisLinesUrl(obligationId) {
    return `${this.$rootScope.apiHost}/api/admin/contracts/${this.project.contractId}`
      + `/funding-obligations/${obligationId}/basis-lines`;
  }

  loadBasisLines(obligationId, force) {
    if (!this.project || !this.project.contractId || !obligationId) {
      return;
    }
    if (!force && (this.basisLinesByObligation[obligationId] || this.basisLinesLoading[obligationId])) {
      return;
    }

    this.basisLinesLoading[obligationId] = true;
    this.$http.get(this.basisLinesUrl(obligationId), { withCredentials: true }).then((success) => {
      this.basisLinesByObligation[obligationId] = success.data || [];
      this.rebuildFundingLineGroups();
      if (this.obligationSort && this.obligationSort.field === 'breakout') {
        this.applyObligationSort();
      }
    }, (failure) => {
      this.basisLinesByObligation[obligationId] = [];
      this.rebuildFundingLineGroups();
      if (this.obligationSort && this.obligationSort.field === 'breakout') {
        this.applyObligationSort();
      }
      const message = (failure && failure.data && failure.data.message) || 'Failed to load basis lines';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.basisLinesLoading[obligationId] = false;
    });
  }

  getObligationBreakoutSummary(obligation) {
    if (!this.supportsBasisLines(obligation)) {
      return '—';
    }
    if (this.isBasisLinesLoading(obligation.id)
        && !Object.prototype.hasOwnProperty.call(this.basisLinesByObligation, obligation.id)) {
      return 'Loading…';
    }

    const lines = this.getBasisLines(obligation.id);
    const count = lines.length;
    if (!count) {
      return 'No labor categories';
    }

    const countLabel = count === 1 ? '1 labor category' : (count + ' labor categories');
    return countLabel
      + ' · '
      + this.formatHours(this.getBasisLinesHoursTotal(obligation.id))
      + ' hrs';
  }

  openManageBasisLinesModal(obligation, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!obligation || !this.supportsBasisLines(obligation) || !this.project || !this.project.contractId) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: manageObligationBasisLinesModal.template,
      controller: manageObligationBasisLinesModal.controller,
      controllerAs: '$ctrl',
      size: manageObligationBasisLinesModal.size || 'lg',
      windowClass: manageObligationBasisLinesModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        project: function () {
          return self.project;
        },
        obligation: function () {
          return obligation;
        },
        obligationLabel: function () {
          return self.obligationOptionLabel(obligation);
        },
        canManage: function () {
          return self.canManage();
        },
        helpers: function () {
          return {
            formatMoney: function (value) { return self.formatMoney(value); },
            formatHours: function (value) { return self.formatHours(value); },
            basisRateTypeLabel: function (line) { return self.basisRateTypeLabel(line); }
          };
        }
      }
    });

    modalInstance.result.then(function () {
      self.loadBasisLines(obligation.id, true);
    }, function () {
      // dismissed
    });
  }

  openAddBasisLineModal(obligation, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canManage() || !obligation || !this.project || !this.project.contractId) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: addObligationBasisLineModal.template,
      controller: addObligationBasisLineModal.controller,
      controllerAs: '$ctrl',
      size: addObligationBasisLineModal.size || 'md',
      windowClass: addObligationBasisLineModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        contractId: function () {
          return self.project.contractId;
        },
        obligation: function () {
          return obligation;
        },
        obligationLabel: function () {
          return self.obligationOptionLabel(obligation);
        }
      }
    });

    modalInstance.result.then(function () {
      self.expandedObligations[obligation.id] = true;
      self.loadBasisLines(obligation.id, true);
    }, function () {
      // dismissed
    });
  }

  updateBasisLineHours(obligation, line, newHours) {
    if (!this.canManage() || !obligation || !line || !line.id) {
      return this.$q.reject('Cannot update funded hours');
    }

    // Prefer $data from x-editable when present. Treat empty as invalid so we never
    // coerce ''/null to 0 via Number(...) and accidentally wipe funded hours.
    let raw = newHours;
    if (raw === undefined) {
      raw = line.fundedHours;
    }
    if (raw === null || raw === '') {
      return this.$q.reject('Funded hours is required');
    }

    const hours = typeof raw === 'number'
      ? raw
      : parseFloat(String(raw).replace(/,/g, ''), 10);
    if (!isFinite(hours) || hours < 0) {
      return this.$q.reject('Funded hours must be a non-negative number');
    }

    const roundedHours = Math.round(hours * 100000) / 100000;
    this.basisLineSaving[line.id] = true;
    return this.$http.patch(
      `${this.basisLinesUrl(obligation.id)}/${line.id}`,
      { fundedHours: roundedHours },
      { withCredentials: true }
    ).then((success) => {
      const updated = success.data || {};
      line.fundedHours = updated.fundedHours != null ? updated.fundedHours : roundedHours;
      line.fundedAmount = updated.fundedAmount;
      line.rate = updated.rate;
      this.rebuildFundingLineGroups();
      this.toastr.success('Funded hours updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to update funded hours';
      this.toastr.error(message, 'ERROR');
      this.loadBasisLines(obligation.id, true);
      return this.$q.reject(message);
    }).finally(() => {
      this.basisLineSaving[line.id] = false;
    });
  }

  deleteBasisLine(obligation, line, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canManage() || !obligation || !line || !line.id) {
      return;
    }
    if (!window.confirm('Remove this fixed fee basis line from the obligation?')) {
      return;
    }

    this.basisLineSaving[line.id] = true;
    this.$http.delete(
      `${this.basisLinesUrl(obligation.id)}/${line.id}`,
      { withCredentials: true }
    ).then(() => {
      this.toastr.success('Basis line removed');
      this.loadBasisLines(obligation.id, true);
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to remove basis line';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.basisLineSaving[line.id] = false;
    });
  }

  getObligationLineCount(obligation) {
    if (this.supportsBasisLines(obligation)) {
      if (this.isBasisLinesLoading(obligation.id)
          && !Object.prototype.hasOwnProperty.call(this.basisLinesByObligation, obligation.id)) {
        return '…';
      }
      return this.getBasisLines(obligation.id).length;
    }
    return 0;
  }

  getObligationAllocatedAmount(obligation) {
    if (this.supportsBasisLines(obligation)) {
      return this.getBasisLinesAmountTotal(obligation && obligation.id);
    }
    return 0;
  }

  getObligationAttributedAmount(obligation) {
    if (!obligation) {
      return 0;
    }
    return this.getFundingLines().reduce((sum, line) => {
      const attributed = (line.obligationAllocations || [])
        .filter((allocation) => String(allocation.contractFundingObligationId) === String(obligation.id))
        .reduce((inner, allocation) => {
          if (allocation.amount != null && allocation.amount !== '') {
            return inner + (Number(allocation.amount) || 0);
          }
          return inner + ((Number(allocation.hours) || 0) * this.getLineRate(line));
        }, 0);
      return sum + attributed;
    }, 0);
  }

  openViewObligationAttributionModal(obligation, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!obligation) {
      return;
    }

    const self = this;
    const openModal = function (basisLines) {
      const modalInstance = self.$uibModal.open({
        template: viewObligationAttributionModal.template,
        controller: viewObligationAttributionModal.controller,
        controllerAs: '$ctrl',
        size: viewObligationAttributionModal.size || 'lg',
        windowClass: viewObligationAttributionModal.windowClass || 'hmodal-info animated zoomIn',
        resolve: {
          project: function () {
            return self.project;
          },
          obligation: function () {
            return obligation;
          },
          obligationLabel: function () {
            return self.obligationOptionLabel(obligation);
          },
          canManage: function () {
            return self.canManage();
          },
          fundingLines: function () {
            return self.getFundingLines();
          },
          basisLines: function () {
            return basisLines || [];
          },
          helpers: function () {
            return {
              formatMoney: function (value) { return self.formatMoney(value); },
              currency: function (value) { return self.currency(value); },
              getLineRate: function (line) { return self.getLineRate(line); },
              getFundingLineFixedFeeBasisId: function (line) {
                return self.getFundingLineFixedFeeBasisId(line);
              },
              isLineCompatibleWithObligation: function (line, item) {
                if (!line || !line.fundingComponent || !item) {
                  return false;
                }
                return !self.isLineOutOfPlace(line, item);
              }
            };
          }
        }
      });

      modalInstance.result.then(function () {
        return self.refreshProject();
      }, function () {
        // dismissed
      });
    };

    if (!this.supportsBasisLines(obligation)) {
      openModal([]);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(this.basisLinesByObligation, obligation.id)) {
      openModal(this.getBasisLines(obligation.id));
      return;
    }

    this.$http.get(this.basisLinesUrl(obligation.id), { withCredentials: true }).then((success) => {
      this.basisLinesByObligation[obligation.id] = success.data || [];
      openModal(this.basisLinesByObligation[obligation.id]);
    }, (failure) => {
      this.basisLinesByObligation[obligation.id] = [];
      const message = (failure && failure.data && failure.data.message) || 'Failed to load basis lines';
      this.toastr.error(message, 'ERROR');
      openModal([]);
    });
  }

  getObligationAvailableAmount(obligation) {
    return (Number(obligation && obligation.obligatedAmount) || 0)
      - this.getObligationAttributedAmount(obligation);
  }

  isObligationAvailabilityMismatch(obligation) {
    return this.getObligationAvailableAmount(obligation) < -0.009;
  }

  obligationAvailabilityTooltip(obligation) {
    const available = this.getObligationAvailableAmount(obligation);
    if (available < -0.009) {
      return `Charged amount exceeds funded amount by ${this.formatMoney(Math.abs(available))}.`;
    }
    return `${this.formatMoney(available)} of funded amount remains available.`;
  }

  getObligationLaborHours(obligation) {
    if (!this.isLaborObligation(obligation) || !this.supportsBasisLines(obligation)) {
      return 0;
    }
    return this.getBasisLinesHoursTotal(obligation && obligation.id);
  }

  getObligationFeeHours(obligation) {
    if (!this.isFeeObligation(obligation) || !this.supportsBasisLines(obligation)) {
      return 0;
    }
    return this.getBasisLinesHoursTotal(obligation && obligation.id);
  }

  getTotalObligated() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + (Number(obligation && obligation.obligatedAmount) || 0);
    }, 0);
  }

  getTotalLaborHours() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + this.getObligationLaborHours(obligation);
    }, 0);
  }

  getTotalFeeHours() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + this.getObligationFeeHours(obligation);
    }, 0);
  }

  getTotalAllocated() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + this.getObligationAllocatedAmount(obligation);
    }, 0);
  }

  getTotalAttributed() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + this.getObligationAttributedAmount(obligation);
    }, 0);
  }

  getTotalAvailable() {
    return (this.obligations || []).reduce((sum, obligation) => {
      return sum + this.getObligationAvailableAmount(obligation);
    }, 0);
  }

  isTotalAvailabilityMismatch() {
    return this.getTotalAvailable() < -0.009;
  }

  totalAvailabilityTooltip() {
    const available = this.getTotalAvailable();
    if (available < -0.009) {
      return `Charged amount exceeds funded amount by ${this.formatMoney(Math.abs(available))}.`;
    }
    return `${this.formatMoney(available)} of funded amount remains available.`;
  }

  isTotalAllocationMismatch() {
    return Math.abs(this.getTotalObligated() - this.getTotalAllocated()) > 0.009;
  }

  totalAllocationMismatchTooltip() {
    const delta = this.getTotalObligated() - this.getTotalAllocated();
    if (delta > 0) {
      return `${this.formatMoney(delta)} of obligated funding is not yet allocated.`;
    }
    return `Allocated exceeds obligated by ${this.formatMoney(Math.abs(delta))}.`;
  }

  isAllocationMismatch(obligation) {
    if (!obligation) {
      return false;
    }
    if (this.supportsBasisLines(obligation)
        && this.isBasisLinesLoading(obligation.id)
        && !Object.prototype.hasOwnProperty.call(this.basisLinesByObligation, obligation.id)) {
      return false;
    }
    const obligated = Number(obligation.obligatedAmount) || 0;
    const allocated = this.getObligationAllocatedAmount(obligation);
    return Math.abs(obligated - allocated) > 0.009;
  }

  allocationMismatchTooltip(obligation) {
    const obligated = Number(obligation && obligation.obligatedAmount) || 0;
    const allocated = this.getObligationAllocatedAmount(obligation);
    const delta = obligated - allocated;
    if (delta > 0) {
      return `${this.formatMoney(delta)} of customer funding is not yet distributed to a labor category`;
    }
    return `Distributed labor category funding exceeds customer funding by ${this.formatMoney(Math.abs(delta))}.`;
  }

  getObligationFundedTotal(obligation) {
    const lines = this.getLinesForObligation(obligation && obligation.id);
    if (this.isFeeObligation(obligation)) {
      return lines.reduce((sum, line) => sum + this.getFundedFeeAmount(line), 0);
    }
    return lines.reduce((sum, line) => sum + this.getFundedLaborAmount(line), 0);
  }

  getObligationActualTotal(obligation) {
    const lines = this.getLinesForObligation(obligation && obligation.id);
    if (this.isFeeObligation(obligation)) {
      return lines.reduce((sum, line) => sum + this.getActualFeeCost(line), 0);
    }
    return lines.reduce((sum, line) => sum + this.getActualLaborCost(line), 0);
  }

  getObligationFundedHoursTotal(obligation) {
    return this.getLinesForObligation(obligation && obligation.id)
      .reduce((sum, line) => sum + (Number(line.fundedHours) || 0), 0);
  }

  getObligationActualHoursTotal(obligation) {
    return this.getLinesForObligation(obligation && obligation.id)
      .reduce((sum, line) => sum + this.getActualHours(line), 0);
  }

  getObligationRemainingFunding(obligation) {
    return (Number(obligation && obligation.obligatedAmount) || 0)
      - this.getObligationActualTotal(obligation);
  }

  getFundingLines() {
    return (this.project && this.project.fundingBreakdown) || [];
  }

  getSortedFundingLines() {
    return this.getFundingLines()
      .slice()
      .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
  }

  selectFundingComponentTab(component, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.selectedFundingComponentTab = component === 'FEE' ? 'FEE' : 'LABOR';
  }

  isFundingComponentTabSelected(component) {
    return this.selectedFundingComponentTab === component;
  }

  matchesFundingComponentTab(line, component) {
    const tab = component || this.selectedFundingComponentTab;
    if (tab === 'FEE') {
      return !!(line && line.fundingComponent === 'FEE');
    }
    // Labor tab includes LABOR and any legacy combined lines.
    return !!(line && line.fundingComponent !== 'FEE');
  }

  getFundingLinesForComponent(component) {
    const tab = component || this.selectedFundingComponentTab;
    return this.getSortedFundingLines().filter((line) => this.matchesFundingComponentTab(line, tab));
  }

  getSelectedFundingComponentLines() {
    return this.getFundingLinesForComponent(this.selectedFundingComponentTab);
  }

  getObligationAllocationByFixedFeeBasis() {
    const map = {};
    (this.obligations || []).forEach((obligation) => {
      const basisLines = this.getBasisLines(obligation.id).filter((line) => {
        return line && line.fixedFeeBasisId != null && line.fixedFeeBasisId !== '';
      });
      if (!basisLines.length) {
        return;
      }

      const breakoutTotal = basisLines.reduce((sum, line) => {
        return sum + (Number(line.fundedAmount) || 0);
      }, 0);
      const obligated = Number(obligation.obligatedAmount) || 0;

      basisLines.forEach((line) => {
        const basisId = line.fixedFeeBasisId;
        if (!map[basisId]) {
          map[basisId] = {
            fixedFeeBasisId: basisId,
            laborCategoryName: line.laborCategoryName || null,
            laborCategoryShortName: line.laborCategoryShortName || null,
            modLabel: line.modLabel || null,
            obligationLaborHours: 0,
            obligationFeeHours: 0,
            obligationLaborAmount: 0,
            obligationFeeAmount: 0
          };
        }

        const hours = Number(line.fundedHours) || 0;
        // Apportion this obligation's Funded Amount across its breakout bases so
        // funding-line totals can match Customer Funding Lines Funded Amount.
        const breakoutAmount = Number(line.fundedAmount) || 0;
        const amount = breakoutTotal > 0.009
          ? obligated * (breakoutAmount / breakoutTotal)
          : (obligated / basisLines.length);

        if (this.isLaborObligation(obligation)) {
          map[basisId].obligationLaborHours += hours;
          map[basisId].obligationLaborAmount += amount;
        } else if (this.isFeeObligation(obligation)) {
          map[basisId].obligationFeeHours += hours;
          map[basisId].obligationFeeAmount += amount;
        }
      });
    });
    return map;
  }

  summarizeGroupLineHours(lines) {
    return (lines || []).reduce((totals, line) => {
      const hours = Number(line && line.fundedHours) || 0;
      if (line && line.fundingComponent === 'FEE') {
        totals.lineFeeHours += hours;
      } else {
        // LABOR and legacy combined lines count toward labor hours.
        totals.lineLaborHours += hours;
      }
      return totals;
    }, { lineLaborHours: 0, lineFeeHours: 0 });
  }

  getFixedFeeBasisFundingGroups() {
    return this.fundingLineGroups || [];
  }

  rebuildFundingLineGroups() {
    const allocationByBasis = this.getObligationAllocationByFixedFeeBasis();
    const groupsById = {};
    const orphanLines = [];

    this.getSortedFundingLines().forEach((line) => {
      const basisId = this.getFundingLineFixedFeeBasisId(line);
      if (!basisId) {
        orphanLines.push(line);
        return;
      }

      if (!groupsById[basisId]) {
        const allocation = allocationByBasis[basisId] || {};
        const popRate = line.chargeCode && line.chargeCode.popRate;
        groupsById[basisId] = {
          fixedFeeBasisId: basisId,
          isOrphan: false,
          laborCategoryName: allocation.laborCategoryName
            || (popRate && popRate.laborCategory && popRate.laborCategory.name)
            || 'Labor Category',
          laborCategoryShortName: allocation.laborCategoryShortName
            || (popRate && popRate.laborCategory && popRate.laborCategory.shortName)
            || null,
          modLabel: allocation.modLabel || (popRate && popRate.fixedFeeModLabel) || null,
          obligationLaborHours: allocation.obligationLaborHours || 0,
          obligationFeeHours: allocation.obligationFeeHours || 0,
          obligationLaborAmount: allocation.obligationLaborAmount || 0,
          obligationFeeAmount: allocation.obligationFeeAmount || 0,
          lines: []
        };
      }
      groupsById[basisId].lines.push(line);
    });

    const groups = Object.keys(groupsById).map((key) => {
      const group = groupsById[key];
      const hourTotals = this.summarizeGroupLineHours(group.lines);
      group.lineLaborHours = hourTotals.lineLaborHours;
      group.lineFeeHours = hourTotals.lineFeeHours;
      group.groupKey = String(group.fixedFeeBasisId);
      return group;
    }).sort((a, b) => {
      const categoryCompare = (a.laborCategoryName || '').localeCompare(b.laborCategoryName || '');
      if (categoryCompare !== 0) {
        return categoryCompare;
      }
      return (a.modLabel || '').localeCompare(b.modLabel || '');
    });

    if (orphanLines.length) {
      const hourTotals = this.summarizeGroupLineHours(orphanLines);
      groups.push({
        fixedFeeBasisId: null,
        groupKey: 'orphan',
        isOrphan: true,
        laborCategoryName: 'Unassigned',
        laborCategoryShortName: 'Unassigned',
        modLabel: null,
        obligationLaborHours: 0,
        obligationFeeHours: 0,
        obligationLaborAmount: 0,
        obligationFeeAmount: 0,
        lineLaborHours: hourTotals.lineLaborHours,
        lineFeeHours: hourTotals.lineFeeHours,
        lines: orphanLines
      });
    }

    this.fundingLineGroups = groups;
    this.ensureSelectedFundingLineGroup();
    return groups;
  }

  fundingLineGroupKey(group) {
    if (!group) {
      return null;
    }
    return group.groupKey || (group.fixedFeeBasisId != null ? String(group.fixedFeeBasisId) : 'orphan');
  }

  ensureSelectedFundingLineGroup() {
    const groups = this.fundingLineGroups || [];
    if (!groups.length) {
      this.selectedFundingLineGroupKey = null;
      this.selectedFundingLineGroup = null;
      return;
    }
    const stillExists = groups.some((group) => {
      return this.fundingLineGroupKey(group) === this.selectedFundingLineGroupKey;
    });
    if (!stillExists) {
      this.selectedFundingLineGroupKey = this.fundingLineGroupKey(groups[0]);
    }
    this.selectedFundingLineGroup = groups.find((group) => {
      return this.fundingLineGroupKey(group) === this.selectedFundingLineGroupKey;
    }) || groups[0] || null;
  }

  selectFundingLineGroup(group, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.selectedFundingLineGroupKey = this.fundingLineGroupKey(group);
    this.selectedFundingLineGroup = group || null;
  }

  isFundingLineGroupSelected(group) {
    return this.fundingLineGroupKey(group) === this.selectedFundingLineGroupKey;
  }

  getSelectedFundingLineGroup() {
    return this.selectedFundingLineGroup;
  }

  canBalanceFundingLineGroup(group) {
    const target = group || this.selectedFundingLineGroup;
    if (!this.canManage() || !target || target.isOrphan || !target.fixedFeeBasisId) {
      return false;
    }
    if (this.fundingLineGroupBalancing) {
      return false;
    }
    const lines = target.lines || [];
    if (!lines.length) {
      return false;
    }
    const laborLines = lines.filter((line) => line && line.fundingComponent !== 'FEE');
    const feeLines = lines.filter((line) => line && line.fundingComponent === 'FEE');
    const laborRateSum = laborLines.reduce((sum, line) => sum + (this.getLineRate(line) || 0), 0);
    const feeRateSum = feeLines.reduce((sum, line) => sum + (this.getLineRate(line) || 0), 0);
    const canBalanceLabor = laborLines.length > 0 && laborRateSum > 0;
    const canBalanceFee = feeLines.length > 0 && feeRateSum > 0;
    return canBalanceLabor || canBalanceFee;
  }

  fundingLineGroupNeedsBalance(group) {
    if (!this.canBalanceFundingLineGroup(group)) {
      return false;
    }
    const updates = this.buildEqualHourBalanceUpdatesForGroup(group);
    return updates.some((update) => {
      const current = Number(update.line.fundedHours) || 0;
      return Math.abs(current - update.fundedHours) > 0.00001;
    });
  }

  getBalanceableFundingLineGroups() {
    return (this.fundingLineGroups || []).filter((group) => this.fundingLineGroupNeedsBalance(group));
  }

  canBalanceAnyFundingLineGroup() {
    return this.canManage()
      && !this.fundingLineGroupBalancing
      && this.getBalanceableFundingLineGroups().length > 0;
  }

  isFundingLineGroupBalancing() {
    return !!this.fundingLineGroupBalancing;
  }

  /**
   * Equal hours per line within each Labor/Fee bucket such that
   * sum(hours * lineRate) equals the customer-funded $ for that bucket.
   * hours = targetAmount / sum(rates)
   */
  buildEqualHourBalanceUpdatesForBucket(lines, targetAmount) {
    const usable = (lines || []).filter((line) => line && (this.getLineRate(line) || 0) > 0);
    if (!usable.length) {
      return [];
    }
    const rateSum = usable.reduce((sum, line) => sum + this.getLineRate(line), 0);
    if (!(rateSum > 0)) {
      return [];
    }

    const target = Number(targetAmount) || 0;
    let hours = target / rateSum;
    // Keep hours equal across lines, but correct floating-point / rounding drift so
    // sum(hours * rate) lands on the customer-funded dollar target.
    let achieved = usable.reduce((sum, line) => sum + (hours * this.getLineRate(line)), 0);
    if (achieved > 0 && Math.abs(achieved - target) > 0.0000001) {
      hours = hours * (target / achieved);
    }
    hours = Math.round(hours * 100000000) / 100000000;
    achieved = usable.reduce((sum, line) => sum + (hours * this.getLineRate(line)), 0);
    if (achieved > 0 && Math.abs(achieved - target) > 0.009) {
      hours = Math.round((hours * (target / achieved)) * 100000000) / 100000000;
    }

    return usable.map((line) => ({
      line,
      fundedHours: hours
    }));
  }

  buildEqualHourBalanceUpdatesForGroup(group) {
    if (!group || group.isOrphan) {
      return [];
    }
    const lines = group.lines || [];
    const laborLines = lines.filter((line) => line && line.fundingComponent !== 'FEE');
    const feeLines = lines.filter((line) => line && line.fundingComponent === 'FEE');
    return this.buildEqualHourBalanceUpdatesForBucket(laborLines, group.obligationLaborAmount)
      .concat(this.buildEqualHourBalanceUpdatesForBucket(feeLines, group.obligationFeeAmount));
  }

  patchFundingLineFundedHours(line, fundedHours) {
    return this.$http.patch(
      `${this.$rootScope.apiHost}/api/admin/projects/${this.project.id}/funding/${line.id}`,
      { fundedHours },
      { withCredentials: true }
    );
  }

  applyEqualHourBalanceForGroup(group) {
    const updates = this.buildEqualHourBalanceUpdatesForGroup(group)
      .filter((update) => {
        const current = Number(update.line.fundedHours) || 0;
        return Math.abs(current - update.fundedHours) > 0.00001;
      });
    if (!updates.length) {
      return this.$q.resolve([]);
    }
    return this.$q.all(updates.map((update) => {
      return this.patchFundingLineFundedHours(update.line, update.fundedHours);
    }));
  }

  balanceAllFundingLineGroupHours($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canBalanceAnyFundingLineGroup() || !this.project || !this.project.id) {
      return;
    }

    const groups = this.getBalanceableFundingLineGroups();
    const confirmed = window.confirm(
      'Balance funded hours for ' + groups.length + ' fixed fee basis'
      + (groups.length === 1 ? '' : 'es') + '?\n\n'
      + 'For each basis, labor and fee are handled separately. Customer-funded $ '
      + 'for that basis is converted to equal hours on every funding line in the '
      + 'bucket, using each line’s own rate (not contract rates).'
    );
    if (!confirmed) {
      return;
    }

    this.fundingLineGroupBalancing = true;
    this.$q.all(groups.map((group) => this.applyEqualHourBalanceForGroup(group))).then(() => {
      this.toastr.success('Funded hours balanced for ' + groups.length
        + ' fixed fee basis' + (groups.length === 1 ? '' : 'es'));
      return this.refreshProject();
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Failed to balance funding line hours';
      this.toastr.error(message, 'ERROR');
      return this.refreshProject();
    }).finally(() => {
      this.fundingLineGroupBalancing = false;
    });
  }

  sortFundingLines(field, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.fundingLineSort) {
      this.fundingLineSort = { field: 'lineNumber', reverse: false };
    }
    if (this.fundingLineSort.field === field) {
      this.fundingLineSort.reverse = !this.fundingLineSort.reverse;
    } else {
      this.fundingLineSort.field = field;
      this.fundingLineSort.reverse = false;
    }
  }

  fundingLineSortIcon(field) {
    if (!this.fundingLineSort || this.fundingLineSort.field !== field) {
      return 'fa-sort';
    }
    return this.fundingLineSort.reverse ? 'fa-sort-desc' : 'fa-sort-asc';
  }

  fundingLineSortValue(line, field) {
    if (!line) {
      return null;
    }
    switch (field) {
      case 'lineNumber':
        return Number(line.lineNumber) || 0;
      case 'component':
        return (this.componentLabel(line) || '').toLowerCase();
      case 'user': {
        const first = line.user && line.user.firstName ? line.user.firstName : '';
        const last = line.user && line.user.lastName ? line.user.lastName : '';
        return (last + ' ' + first).trim().toLowerCase();
      }
      case 'chargeCode':
        return (line.chargeCode && line.chargeCode.name ? line.chargeCode.name : '').toLowerCase();
      case 'fixedFeeBasis':
        return (this.fundingLineFixedFeeBasisLabel(line) || '').toLowerCase();
      case 'rate':
        return this.getLineRate(line);
      case 'fundedHours':
        return Number(line.fundedHours) || 0;
      case 'totalFunded':
        return this.getTotalFundedAmount(line);
      case 'actualHours':
        return this.getActualHours(line);
      case 'actualSpent':
        return this.getActualSpentAmount(line);
      case 'remainingHours':
        return this.getRemainingHours(line);
      case 'remainingFunding':
        return this.getRemainingFunding(line);
      default:
        return null;
    }
  }

  fundingLineGroupFullLabel(group) {
    if (!group) {
      return '';
    }
    if (group.isOrphan) {
      return 'Unassigned';
    }
    const parts = [group.laborCategoryName || 'Labor Category'];
    if (group.modLabel) {
      parts.push(group.modLabel);
    }
    return parts.join(' · ');
  }

  fundingLineGroupShortCategory(group) {
    if (!group) {
      return '';
    }
    if (group.isOrphan) {
      return 'Unassigned';
    }
    return group.laborCategoryShortName
      || group.laborCategoryName
      || 'Labor Category';
  }

  fundingLineGroupTabLabel(group) {
    if (!group) {
      return '';
    }
    if (group.isOrphan) {
      return 'Unassigned';
    }
    const category = this.fundingLineGroupShortCategory(group);
    // Keep tabs short: LCAT short name only, unless the same LCAT has multiple mods.
    if (!group.modLabel || !this.fundingLineGroupCategoryNeedsModDisambiguation(group)) {
      return category;
    }
    return category + ' · ' + group.modLabel;
  }

  fundingLineGroupCategoryNeedsModDisambiguation(group) {
    if (!group || group.isOrphan) {
      return false;
    }
    const shortKey = this.fundingLineGroupShortCategory(group);
    const nameKey = group.laborCategoryName || 'Labor Category';
    const groups = this.fundingLineGroups || [];
    let count = 0;
    for (let i = 0; i < groups.length; i++) {
      const candidate = groups[i];
      if (candidate.isOrphan) {
        continue;
      }
      const sameShort = this.fundingLineGroupShortCategory(candidate) === shortKey;
      const sameName = (candidate.laborCategoryName || 'Labor Category') === nameKey;
      if (sameShort || sameName) {
        count += 1;
        if (count > 1) {
          return true;
        }
      }
    }
    return false;
  }

  getGroupHourUniqueKey(line) {
    const chargeCodeId = line && line.chargeCode && line.chargeCode.id;
    const userId = line && line.user && line.user.id;
    return `${chargeCodeId || 'cc'}:${userId || 'user'}`;
  }

  getGroupUniqueHoursTotal(group, getHoursFn) {
    const hoursByKey = {};
    ((group && group.lines) || []).forEach((line) => {
      const key = this.getGroupHourUniqueKey(line);
      const hours = Number(getHoursFn(line)) || 0;
      // Labor/Fee pairs share the same charge code + user hours. Keep one value
      // per pair (prefer LABOR / combined over FEE when both exist).
      if (!Object.prototype.hasOwnProperty.call(hoursByKey, key)) {
        hoursByKey[key] = hours;
        return;
      }
      if (line && line.fundingComponent === 'FEE') {
        return;
      }
      hoursByKey[key] = hours;
    });
    return Object.keys(hoursByKey).reduce((sum, key) => sum + (hoursByKey[key] || 0), 0);
  }

  getGroupFundedHoursTotal(group) {
    return this.getGroupUniqueHoursTotal(group, (line) => Number(line && line.fundedHours) || 0);
  }

  getGroupActualHoursTotal(group) {
    return this.getGroupUniqueHoursTotal(group, (line) => this.getActualHours(line));
  }

  getGroupFundedLaborTotal(group) {
    return ((group && group.lines) || [])
      .reduce((sum, line) => sum + this.getFundedLaborAmount(line), 0);
  }

  getGroupFundedFeeTotal(group) {
    return ((group && group.lines) || [])
      .reduce((sum, line) => sum + this.getFundedFeeAmount(line), 0);
  }

  getGroupTotalFundedAmount(group) {
    return ((group && group.lines) || [])
      .reduce((sum, line) => sum + this.getTotalFundedAmount(line), 0);
  }

  getGroupActualSpentTotal(group) {
    return ((group && group.lines) || [])
      .reduce((sum, line) => sum + this.getActualSpentAmount(line), 0);
  }

  getGroupRemainingHoursTotal(group) {
    return this.getGroupFundedHoursTotal(group) - this.getGroupActualHoursTotal(group);
  }

  getGroupRemainingFundingTotal(group) {
    return this.getGroupTotalFundedAmount(group) - this.getGroupActualSpentTotal(group);
  }

  isGroupRemainingHoursNegative(group) {
    return this.getGroupRemainingHoursTotal(group) < -0.009;
  }

  isGroupRemainingFundingNegative(group) {
    return this.getGroupRemainingFundingTotal(group) < -0.009;
  }

  getFundingLineGroupForLine(line) {
    if (!line) {
      return null;
    }
    const basisId = this.getFundingLineFixedFeeBasisId(line);
    const key = basisId != null && basisId !== '' ? String(basisId) : 'orphan';
    return (this.fundingLineGroups || []).find((group) => this.fundingLineGroupKey(group) === key) || null;
  }

  fundingLineFixedFeeBasisLabel(line) {
    const group = this.getFundingLineGroupForLine(line);
    if (!group) {
      return '—';
    }
    return this.fundingLineGroupFullLabel(group) || '—';
  }

  isFirstFundingLineInGroup(line) {
    const group = this.getFundingLineGroupForLine(line);
    if (!group || !group.lines || !group.lines.length || !line) {
      return false;
    }
    const first = group.lines.slice().sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0))[0];
    return !!(first && String(first.id) === String(line.id));
  }

  getFundingLinesTotalFundedAmount() {
    return this.getSelectedFundingComponentLines()
      .reduce((sum, line) => sum + this.getTotalFundedAmount(line), 0);
  }

  getFundingLinesActualSpentTotal() {
    return this.getSelectedFundingComponentLines()
      .reduce((sum, line) => sum + this.getActualSpentAmount(line), 0);
  }

  getFundingLinesRemainingHoursTotal() {
    return this.getFundingLinesFundedHoursTotal() - this.getFundingLinesActualHoursTotal();
  }

  getFundingLinesRemainingFundingTotal() {
    return this.getFundingLinesTotalFundedAmount() - this.getFundingLinesActualSpentTotal();
  }

  isFundingLinesRemainingHoursNegative() {
    return this.getFundingLinesRemainingHoursTotal() < -0.009;
  }

  isFundingLinesRemainingFundingNegative() {
    return this.getFundingLinesRemainingFundingTotal() < -0.009;
  }

  getFundingLinesFundedHoursTotal() {
    return this.getSelectedFundingComponentLines()
      .reduce((sum, line) => sum + (Number(line.fundedHours) || 0), 0);
  }

  getFundingLinesActualHoursTotal() {
    return this.getSelectedFundingComponentLines()
      .reduce((sum, line) => sum + this.getActualHours(line), 0);
  }

  getFundingLinesFundedLaborTotal() {
    return this.getSortedFundingLines()
      .reduce((sum, line) => sum + this.getFundedLaborAmount(line), 0);
  }

  getFundingLinesFundedFeeTotal() {
    return this.getSortedFundingLines()
      .reduce((sum, line) => sum + this.getFundedFeeAmount(line), 0);
  }

  getLinesForObligation(obligationId) {
    return this.getFundingLines()
      .filter((line) => String(line.contractFundingObligationId) === String(obligationId))
      .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
  }

  getUnassociatedLines() {
    // Temporarily treat all funding lines as unassociated in the UI until
    // obligation association requirements are finalized. Association APIs /
    // modals remain available but lines are no longer shown under obligations.
    return this.getFundingLines()
      .slice()
      .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
  }

  hasUnassociatedLines() {
    return this.getUnassociatedLines().length > 0;
  }

  getUnassociatedLineCount() {
    return this.getUnassociatedLines().length;
  }

  needsSplitMigration() {
    return this.getFundingLines().some((line) => !line.fundingComponent);
  }

  laborObligations() {
    return (this.obligations || []).filter((o) => o.costElement === 'LABOR');
  }

  feeObligations() {
    return (this.obligations || []).filter((o) => o.costElement === 'FIXED_FEE');
  }

  obligationsForLine(line) {
    if (!line || !line.fundingComponent) {
      return this.obligations || [];
    }
    if (line.fundingComponent === 'LABOR') {
      return this.laborObligations();
    }
    if (line.fundingComponent === 'FEE') {
      return this.feeObligations();
    }
    return this.obligations || [];
  }

  obligationOptionLabel(obligation) {
    const parts = [
      obligation.periodOfPerformanceName || 'Option Year',
      'CLIN ' + (obligation.clin || '—'),
      obligation.slin ? ('SLIN ' + obligation.slin) : null,
      'ACRN ' + (obligation.acrn || '—'),
      this.costElementLabel(obligation)
    ].filter(Boolean);
    return parts.join(' · ');
  }

  loadObligations() {
    if (!this.project || !this.project.contractId || !this.project.id) {
      this.obligations = [];
      this.sortedObligations = [];
      return;
    }

    this.loading = true;
    const url = `${this.$rootScope.apiHost}/api/admin/contracts/${this.project.contractId}/funding-obligations`
      + `?page=0&size=10000&sort=periodOfPerformanceName,asc&taskOrderId=${encodeURIComponent(this.project.id)}`;

    this.$http.get(url, { withCredentials: true }).then((success) => {
      const payload = success.data || {};
      const page = payload.page || payload;
      this.obligations = page.content || [];
      this.applyObligationSort();
      this.basisLinesByObligation = {};
      this.rebuildFundingLineGroups();
      (this.obligations || []).forEach((obligation) => {
        if (this.supportsBasisLines(obligation)) {
          this.loadBasisLines(obligation.id, true);
        }
      });
    }, (failure) => {
      this.obligations = [];
      this.sortedObligations = [];
      this.rebuildFundingLineGroups();
      const message = (failure && failure.data && failure.data.message) || 'Failed to load funding obligations';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.loading = false;
    });
  }

  refreshProject() {
    if (typeof this.onChanged === 'function') {
      return Promise.resolve(this.onChanged());
    }
    return Promise.resolve();
  }

  openAddFundingPairModal() {
    if (!this.canManage() || !this.project || !this.project.id) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: addFundingPairModal.template,
      controller: addFundingPairModal.controller,
      controllerAs: '$ctrl',
      size: addFundingPairModal.size || 'md',
      windowClass: addFundingPairModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        project: function () {
          return self.project;
        }
      }
    });

    modalInstance.result.then(function () {
      return self.refreshProject();
    }, function () {
      // dismissed
    });
  }

  openReassignFundingLineModal(line, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canManage() || !line || !this.project || !this.project.id) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: reassignFundingLineModal.template,
      controller: reassignFundingLineModal.controller,
      controllerAs: '$ctrl',
      size: reassignFundingLineModal.size || 'md',
      windowClass: reassignFundingLineModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        project: function () {
          return self.project;
        },
        line: function () {
          return line;
        },
        obligations: function () {
          return self.obligationsForLine(line);
        },
        obligationOptionLabel: function () {
          return function (obligation) {
            return self.obligationOptionLabel(obligation);
          };
        }
      }
    });

    modalInstance.result.then(function () {
      return self.refreshProject();
    }, function () {
      // dismissed
    });
  }

  openUnassociatedFundingLinesModal($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.project || !this.project.id || !this.hasUnassociatedLines()) {
      return;
    }

    const self = this;
    const modalInstance = this.$uibModal.open({
      template: unassociatedFundingLinesModal.template,
      controller: unassociatedFundingLinesModal.controller,
      controllerAs: '$ctrl',
      size: unassociatedFundingLinesModal.size || 'lg',
      windowClass: unassociatedFundingLinesModal.windowClass || 'hmodal-info animated zoomIn',
      resolve: {
        project: function () {
          return self.project;
        },
        lines: function () {
          return self.getUnassociatedLines();
        },
        obligations: function () {
          return self.obligations || [];
        },
        canManage: function () {
          return self.canManage();
        },
        helpers: function () {
          return {
            formatMoney: function (value) { return self.formatMoney(value); },
            currency: function (value) { return self.currency(value); },
            componentLabel: function (line) { return self.componentLabel(line); },
            getFullyBurdenedRate: function (line) { return self.getFullyBurdenedRate(line); },
            getFeeRate: function (line) { return self.getFeeRate(line); },
            getFundedLaborAmount: function (line) { return self.getFundedLaborAmount(line); },
            getFundedFeeAmount: function (line) { return self.getFundedFeeAmount(line); },
            getActualHours: function (line) { return self.getActualHours(line); },
            getActualLaborCost: function (line) { return self.getActualLaborCost(line); },
            obligationOptionLabel: function (obligation) { return self.obligationOptionLabel(obligation); },
            obligationsForLine: function (line) { return self.obligationsForLine(line); }
          };
        }
      }
    });

    modalInstance.result.then(function () {
      return self.refreshProject();
    }, function () {
      // dismissed
    });
  }

  migrateSplit() {
    if (!this.canManage() || this.migrating) {
      return;
    }

    this.migrating = true;
    this.$http.post(
      `${this.$rootScope.apiHost}/api/admin/projects/${this.project.id}/funding/breakdown/migrate-split`,
      {},
      { withCredentials: true }
    ).then((success) => {
      const result = success.data || {};
      this.toastr.success(
        `Split ${result.splitCount || 0} funding line(s)`
        + (result.skippedCount ? ` (${result.skippedCount} already split)` : '')
      );
      return this.refreshProject();
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to migrate funding lines';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.migrating = false;
    });
  }

  updateFundedHours(line) {
    if (!line || !this.canManage()) {
      return;
    }

    return this.$http.patch(
      `${this.$rootScope.apiHost}/api/admin/projects/${this.project.id}/funding/${line.id}`,
      { fundedHours: line.fundedHours },
      { withCredentials: true }
    ).then(() => {
      this.toastr.success('Funded hours updated');
      return this.refreshProject();
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to update funded hours';
      this.toastr.error(message, 'ERROR');
      return this.refreshProject();
    });
  }
}

export default ObligationFundingPanelController;



// WEBPACK FOOTER //
// ./client/pages/admin/project-details/views/obligation-funding-panel/obligation-funding-panel.controller.js